<template>
    <div class="flex flex-col gap-12 shadow bg-white rounded p-16 border border-white">
        <div class="flex justify-between">
            <h2 class="flex items-center font-semibold text-heading-2">
                <svg-icon type="description" class="text-primary w-24 h-24 mr-8"/>
                {{ $t('interims.attachments.title') }}
            </h2>
            <simple-button
                class="ms-auto"
                icon="add-circle"
                look="primary"
                small
                v-if="canManage"
                @click="startCreatingAttachment"
            >
                {{ $t('interims.attachments.add_new') }}
            </simple-button>
        </div>

        <div v-if="attachments.length > 0">
            <div v-for="(attachment, i) in attachments" :key="attachment.id"
                 class="border border-primary-20 flex items-center px-12 py-8 relative"
                 :class="[i !== 0 ? 'border-t-0' : 'rounded-t', i === attachments.length - 1 ? 'rounded-b' : '']">
                <a
                    :target="extensions.includes(attachment.extension) ? '_blank' : null"
                    :href="attachment.url"
                    :download="!extensions.includes(attachment.extension) ? attachment.original_name : null"
                    class="group flex items-center cursor-pointer flex-grow w-full"
                >
                    <div class="mr-auto group-hover:text-primary trans-ease" style="min-width: 1px">
                        <h3 class="font-semibold text-body-sm leading-tight mb-2 truncate">
                            {{ attachment.title }}
                        </h3>
                        <p class="text-body-sm leading-none break-all">{{ attachment.original_name }}</p>
                    </div>
                    <div class="pl-16">
                        <div
                            class="h-32 w-32 flex items-center justify-center rounded-6 text-primary-80 trans-ease group-hover:text-primary">
                            <svg-icon type="download" class="max-w-16 max-h-16"></svg-icon>
                        </div>
                    </div>
                </a>
                <div v-if="canManage">
                    <simple-button
                        display="danger"
                        icon="delete"
                        look="icon"
                        small
                        @click="startDeletingAttachment(attachment)"
                    />
                </div>
            </div>
        </div>

        <simple-button
            class="ms-auto"
            icon="add-circle"
            look="primary"
            small
            v-if="canManage && attachments.length > 8"
            @click="startCreatingAttachment"
        >
            {{ $t('interims.internal_notes.add_new') }}
        </simple-button>

        <popup-modal v-if="showingAttachment" @close="stopShowingAttachment" :title="showingAttachment ? showingAttachment.title : null"
                     position="center">
            <div class="w-600" v-html="showingAttachment ? showingAttachment.content : null"></div>
        </popup-modal>

        <edit-resource
            custom-class="w-764"
            v-if="creatingAttachment"
            :title="$t('interims.attachments.form.title')"
            :form="form"
            @close="stopCreatingAttachment"
            @submit="addAttachment"
        >
            <simple-input v-model="form.title" :errors="form.errors.title" class="mb-32">
                {{ $t('interims.attachments.form.name') }}
            </simple-input>
            <file-upload v-model="form.queued_file_id">
                {{ $t('interims.attachments.form.file') }}
            </file-upload>
        </edit-resource>

        <popup-modal-confirm
            v-if="deletingAttachment"
            show-exclamation
            :message="$t('general.delete-attachment')"
            :working="performingDeleteRequest"
            @close="stopDeletingAttachment"
            @confirm="deleteAttachment"
        />
    </div>
</template>

<script>
import SvgIcon from './ui/SvgIcon';
import Interim from "../models/Interim";
import PopupModal from "./ui/PopupModal";
import SimpleButton from "./ui/SimpleButton";
import Form from "../etc/forms/Form";
import EditResource from "./ui/EditResource";
import SimpleInput from "./forms/Input";
import Wysiwyg from "./forms/Wysiwyg";
import * as interims from '../services/InterimService';
import {normalizeJson, openableFileExtensionsInWebBrowser} from "../../shared/helpers";
import PopupModalConfirm from "./ui/PopupModalConfirm";
import FileUpload from "./forms/FileUpload";
import InterimAttachment from "../models/InterimAttachment";
import {useRepo} from 'pinia-orm';

export default {
    components: {FileUpload, PopupModalConfirm, Wysiwyg, SimpleInput, EditResource, SimpleButton, PopupModal, SvgIcon},
    props: {
        interim: Interim,
        canManage: Boolean,
    },
    data() {
        return {
            showingAttachment: false,

            creatingAttachment: false,
            form: this.generateForm(),

            deletingAttachment: false,
            performingDeleteRequest: false
        }
    },
    computed: {
        attachments() {
            if (!this.interim)
                return [];

            return this.interim.attachments;
        },
        extensions() {
            return openableFileExtensionsInWebBrowser
        }
    },
    methods: {
        generateForm() {
            return new Form([
                {name: 'title', rules: ['required']},
                {name: 'queued_file_id', rules: ['required']}
            ]);
        },
        startShowingAttachment(attachment) {
            this.showingAttachment = attachment;
        },
        stopShowingAttachment() {
            this.showingAttachment = false;
        },
        startCreatingAttachment() {
            this.creatingAttachment = true;
        },
        stopCreatingAttachment() {
            this.creatingAttachment = false;
        },
        startDeletingAttachment(attachment) {
            this.deletingAttachment = attachment;
        },
        stopDeletingAttachment() {
            this.deletingAttachment = false;
        },

        addAttachment() {
            if (!this.form || !this.form.queued_file_id || !this.form.title) {
                return;
            }
            this.form.submit(data => {
                return interims.storeAttachment(this.interim.id, {
                    data: {
                        attributes: data
                    }
                })
            }).then(result => {
                return useRepo(Interim).save(normalizeJson(result))
            }).then(() => {
                this.stopCreatingAttachment();
                this.form = this.generateForm();
            })
        },
        deleteAttachment() {
            this.performingDeleteRequest = true;

            interims.destroyAttachment(this.interim.id, this.deletingAttachment.id)
            .then(result => {
                return useRepo(Interim).save(normalizeJson(result))
            }).then(() => {
                return useRepo(InterimAttachment).destroy(this.deletingAttachment.id)
            }).then(() => {
                this.stopDeletingAttachment();
                this.performingDeleteRequest = false;
            })
        }
    }
}
</script>
